/**
 * Draws the sim's shots (`state.shots`) as projectiles from fixed instanced
 * pools: tracers, arrows, shells, missiles and beams, with hit bursts and
 * miss dirt (models/impacts.ts). Fog-safe: only the visible stretch of a
 * shot's flight is drawn (shots.ts `visibleSpan`).
 */
import { useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import { axialToWorld, getHexTileMapKey } from '@/game/hexmap';
import config from '@/game/hexmap/config';
import type { HexTileMapKey } from '@/game/hexmap/model/types';
import { buildingLevel, fromMilli, type Shot, type SimState } from '@/game/sim';
import { advanceDisplayTick, DISPLAY_LAG, shotTick, visibleSpan } from '@/game/view/shots';
import { useLiveReaders } from './app/simFrame';
import {
  BURST_GEOMETRY,
  BURST_MATERIAL,
  burstScale,
  CLOD_GEOMETRY,
  CLOD_LIFE,
  CLOD_MATERIAL,
  clodHeight,
  DIRT_CLODS,
  DUST_GEOMETRY,
  DUST_LIFE,
  DUST_MATERIAL,
  dustScale,
  FLASH_COLOR,
  FLASH_GEOMETRY,
  FLASH_MATERIAL,
  fade,
  fadeColor,
  flashScale,
  HIT_BURST_LIFE,
  HIT_FLASH_LIFE,
  IMPACT_VISUALS,
  muzzleFor,
  SPARK_COLORS,
  SPARK_GEOMETRY,
  SPARK_LIFE,
  SPARK_MATERIAL,
  SPARK_WIDTH,
  sparkAt,
  sparkDirection,
  TARGET_FLASH,
  TARGET_FLASH_GEOMETRY,
  TARGET_FLASH_MATERIAL,
} from './models';

type ShotKind = Shot['kind'];

/** Sim seconds per tick. */
const DT = 0.1;
const TILE_RADIUS = config.hexMap.tile.radius;
const GROUND_Y = config.hexMap.tile.thickness;
/** Where a shot leaves a unit / a building, and where it lands on either or the ground. */
const UNIT_HIT_Y = GROUND_Y + 0.25;
const BUILDING_HIT_Y = GROUND_Y + 0.4;
const MISS_Y = GROUND_Y + 0.04;

/** Caps on effects alive at once: shots past the cap aren't drawn; a new effect replaces the oldest. */
const MAX_SHOTS = 192;
const MAX_BURSTS = 64;
const MAX_PUFFS = 320;
const MAX_DUST = 128;
const MAX_DIRT = 384;
const MAX_SPARKS = 384;

/** How each kind of shot arcs: apex height per world unit of distance, plus lift. */
const ARC: Record<ShotKind, { arc: number; lift: number }> = {
  tracer: { arc: 0, lift: 0 },
  arrow: { arc: 0.18, lift: 0.1 },
  shell: { arc: 0.4, lift: 0.5 },
  missile: { arc: 0.28, lift: 0.5 },
  beam: { arc: 0, lift: 0 },
};
const TRACER_LENGTH = 0.45;
const BEAM_WIDTH = 0.06;
/** Ticks a beam stays up past its impact, thinning away. */
const BEAM_FADE_TICKS = 2;
/** A shot whose impact is further back than this (ticks) when first drawn gets no impact effect. */
const STALE_IMPACT_TICKS = 3;
/** Seconds of real time a missile's smoke puffs last (impacts: models/impacts.ts). */
const PUFF_LIFE = 0.8;
/** Ticks of flight between two smoke puffs left by a missile. */
const PUFF_EVERY_TICKS = 0.35;

type EffectName = 'flash' | 'burst' | 'targetFlash' | 'dust' | 'dirt' | 'spark' | 'puff';
type MeshName = ShotKind | EffectName;
const MESH_NAMES: readonly MeshName[] = [
  'tracer',
  'arrow',
  'shell',
  'missile',
  'beam',
  'flash',
  'burst',
  'targetFlash',
  'dust',
  'dirt',
  'spark',
  'puff',
];
const EFFECT_NAMES: readonly EffectName[] = [
  'flash',
  'burst',
  'targetFlash',
  'dust',
  'dirt',
  'spark',
  'puff',
];

function emptyCounts(): Record<MeshName, number> {
  return Object.fromEntries(MESH_NAMES.map((name) => [name, 0])) as Record<MeshName, number>;
}

/** One sim shot as drawn: the part of its flight in the viewer's sight, in display ticks. */
interface Drawn {
  active: boolean;
  /** `Shot.id`. */
  id: string;
  kind: ShotKind;
  from: THREE.Vector3;
  to: THREE.Vector3;
  apex: number;
  /** Display ticks the drawn part starts and ends. */
  start: number;
  end: number;
  /** The shot lands in sight: draw its impact at `end`. */
  lands: boolean;
  hit: boolean;
  /** A hit landing on a building (its target flash is building-sized). */
  onBuilding: boolean;
  /** Unit a hit landing in sight follows while it stays in sight (else ''). */
  homing: string;
  /** Where a homing shot is heading now: its target's (interpolated) position. */
  goal: THREE.Vector3;
  impacted: boolean;
  /** Flight fraction (0–1) of the last smoke puff dropped (missiles). */
  lastPuff: number;
  /** State tick the shot was last listed in. */
  seen: number;
}
interface Effect {
  active: boolean;
  pos: THREE.Vector3;
  size: number;
  /** Rise over its life (dust, smoke), clod upward speed, or how far a spark flies. */
  up: number;
  start: number;
  /** Seconds it lasts. */
  life: number;
  /** A spark's direction. */
  dir: THREE.Vector3;
  /** Instance colour at full brightness (additive effects), faded out over its life. */
  color: THREE.Color | null;
}

/** Oldest-first slot to reuse: a free one, else the one started longest ago. */
function freeSlot(pool: readonly Effect[]): Effect {
  let oldest = pool[0];
  for (const slot of pool) {
    if (!slot.active) return slot;
    if (slot.start < oldest.start) oldest = slot;
  }
  return oldest;
}

function effectPool(size: number): Effect[] {
  const pool: Effect[] = [];
  for (let i = 0; i < size; i++) {
    pool.push({
      active: false,
      pos: new THREE.Vector3(),
      size: 0,
      up: 0,
      start: 0,
      life: 1,
      dir: new THREE.Vector3(),
      color: null,
    });
  }
  return pool;
}

function startEffect(
  pool: readonly Effect[],
  pos: THREE.Vector3,
  size: number,
  up: number,
  now: number,
  life: number,
  color: THREE.Color | null = null
): Effect {
  const effect = freeSlot(pool);
  effect.active = true;
  effect.pos.copy(pos);
  effect.size = size;
  effect.up = up;
  effect.start = now;
  effect.life = life;
  effect.color = color;
  return effect;
}

/** Point `t` (0–1) along a shot's drawn flight: straight from→to, plus a parabolic rise. */
function along(shot: Drawn, t: number, out: THREE.Vector3): THREE.Vector3 {
  out.lerpVectors(shot.from, shot.to, t);
  out.y += shot.apex * 4 * t * (1 - t);
  return out;
}

/** A stable fraction in [-0.5, 0.5) from a shot id and a salt, to scatter where misses land. */
function jitter(id: string, salt: number): number {
  let h = 2166136261 ^ salt;
  for (let i = 0; i < id.length; i++) h = Math.imul(h ^ id.charCodeAt(i), 16777619);
  return ((h >>> 0) % 1000) / 1000 - 0.5;
}

/** Writes `obj`'s transform (and `color`) as the next instance of mesh `name`, unless it's full. */
function put(
  meshes: Partial<Record<MeshName, THREE.InstancedMesh>>,
  counts: Record<MeshName, number>,
  name: MeshName,
  obj: THREE.Object3D,
  color?: THREE.Color
): void {
  const mesh = meshes[name];
  if (!mesh || counts[name] >= MESHES[name].max) return;
  obj.updateMatrix();
  if (color) mesh.setColorAt(counts[name], color);
  mesh.setMatrixAt(counts[name]++, obj.matrix);
}

function noRaycast() {}

function basic(color: string): THREE.MeshBasicMaterial {
  return new THREE.MeshBasicMaterial({ color, toneMapped: false });
}

// Unit geometries, stretched per instance; boxes point along +z (Object3D.lookAt).
const boxGeometry = new THREE.BoxGeometry(1, 1, 1);
const ballGeometry = new THREE.IcosahedronGeometry(1, 0);

const MESHES: Record<
  MeshName,
  { geometry: THREE.BufferGeometry; material: THREE.Material; max: number; colored?: boolean }
> = {
  tracer: { geometry: boxGeometry, material: basic('#ffe39a'), max: MAX_SHOTS },
  arrow: { geometry: boxGeometry, material: basic('#e8d6ae'), max: MAX_SHOTS },
  shell: { geometry: ballGeometry, material: basic('#ffc46b'), max: MAX_SHOTS },
  missile: { geometry: boxGeometry, material: basic('#f4f4f4'), max: MAX_SHOTS },
  beam: { geometry: boxGeometry, material: basic('#9ff2ff'), max: MAX_SHOTS },
  // Impacts (models/impacts.ts): the additive ones take a fading instance colour.
  flash: { geometry: FLASH_GEOMETRY, material: FLASH_MATERIAL, max: MAX_BURSTS, colored: true },
  burst: { geometry: BURST_GEOMETRY, material: BURST_MATERIAL, max: MAX_BURSTS },
  targetFlash: {
    geometry: TARGET_FLASH_GEOMETRY,
    material: TARGET_FLASH_MATERIAL,
    max: MAX_BURSTS,
    colored: true,
  },
  dust: { geometry: DUST_GEOMETRY, material: DUST_MATERIAL, max: MAX_DUST },
  dirt: { geometry: CLOD_GEOMETRY, material: CLOD_MATERIAL, max: MAX_DIRT },
  spark: { geometry: SPARK_GEOMETRY, material: SPARK_MATERIAL, max: MAX_SPARKS, colored: true },
  puff: {
    geometry: ballGeometry,
    material: new THREE.MeshStandardMaterial({
      color: '#d4d4d4',
      flatShading: true,
      transparent: true,
      opacity: 0.75,
      depthWrite: false,
    }),
    max: MAX_PUFFS,
  },
};

/**
 * Ranged shots from the sim (`SimState.shots`): each flies from its shooter to
 * where it lands between its firing and impact ticks — a tracer streak, an
 * arrow, an arcing shell, a missile trailing smoke, or an energy beam — on a
 * display clock running smoothly half a tick behind the latest state. A hit
 * flashes white-hot and bursts on its target, lighting it up for a moment;
 * a miss kicks up a dust puff and dirt clods where it lands, with ricochet
 * sparks for bullets, shells and beams (sizes per kind: models/impacts.ts),
 * so the two read apart at game zoom.
 *
 * Fog-safe: a shot is drawn only over the stretch of its line in the viewer's
 * sight (`visibleSpan`): one fired from the fog appears at the edge of sight
 * (with an arc of its visible stretch only, so its range isn't given away),
 * one landing in the fog vanishes at the edge with no impact, and one with
 * neither end in sight isn't drawn. Everything lives in fixed pools drawn as
 * instanced meshes (nothing is allocated per frame) and isn't raycastable.
 */
export function ShotsLayer() {
  const { getState, getTileVisibility } = useLiveReaders();
  const pools = useMemo(() => {
    const shots: Drawn[] = [];
    for (let i = 0; i < MAX_SHOTS; i++) {
      shots.push({
        active: false,
        id: '',
        kind: 'tracer',
        from: new THREE.Vector3(),
        to: new THREE.Vector3(),
        apex: 0,
        start: 0,
        end: 0,
        lands: false,
        hit: false,
        onBuilding: false,
        homing: '',
        goal: new THREE.Vector3(),
        impacted: false,
        lastPuff: 0,
        seen: 0,
      });
    }
    return {
      shots,
      byId: new Map<string, Drawn>(),
      /** Shots done drawing but still lingering in state (id → last tick listed), so they aren't tracked again. */
      done: new Map<string, number>(),
      effects: {
        flash: effectPool(MAX_BURSTS),
        burst: effectPool(MAX_BURSTS),
        targetFlash: effectPool(MAX_BURSTS),
        dust: effectPool(MAX_DUST),
        dirt: effectPool(MAX_DIRT),
        spark: effectPool(MAX_SPARKS),
        puff: effectPool(MAX_PUFFS),
      } satisfies Record<EffectName, Effect[]>,
    };
  }, []);
  const lastTickRef = useRef(-1);
  const displayRef = useRef<number | null>(null);
  const meshRefs = useRef<Partial<Record<MeshName, THREE.InstancedMesh>>>({});
  const scratch = useMemo(
    () => ({
      obj: new THREE.Object3D(),
      ahead: new THREE.Vector3(),
      pos: new THREE.Vector3(),
      dir: new THREE.Vector3(),
      color: new THREE.Color(),
      counts: emptyCounts(),
    }),
    []
  );

  /** Starts drawing a shot the first time it's listed, if any of it is in sight. */
  function track(state: SimState, shot: Shot, display: number): void {
    const isVisible = (key: HexTileMapKey) => getTileVisibility(key) === 'visible';
    const span = visibleSpan(shot.from, shot.to, isVisible);
    if (!span) return;
    let slot: Drawn | undefined;
    for (const s of pools.shots) {
      if (!s.active) {
        slot = s;
        break;
      }
    }
    if (!slot) return;
    const from = axialToWorld(shot.from, TILE_RADIUS);
    const to = axialToWorld(shot.to, TILE_RADIUS);
    // The shooter's model only when its tile is in sight (else the default,
    // so a shot out of the fog never hints at what fired it).
    const shooterBuilding = span.t0 === 0 ? state.buildings[shot.attacker] : undefined;
    const shooterUnit = span.t0 === 0 ? state.units[shot.attacker] : undefined;
    const muzzle = shooterBuilding
      ? muzzleFor(shooterBuilding.type, true, buildingLevel(shooterBuilding))
      : muzzleFor(shooterUnit?.type ?? '', false);
    const landsOnBuilding = span.t1 === 1 && shot.hit && state.buildings[shot.targetId];
    const toY = !shot.hit ? MISS_Y : landsOnBuilding ? BUILDING_HIT_Y : UNIT_HIT_Y;
    const { ahead, pos } = scratch;
    // Full line first, then cut down to its visible stretch.
    pos.set(from.x + muzzle.x, GROUND_Y + muzzle.y, from.z + muzzle.z);
    ahead.set(to.x, toY, to.z);
    if (!shot.hit) {
      ahead.x += jitter(shot.id, 1) * TILE_RADIUS * 0.8;
      ahead.z += jitter(shot.id, 2) * TILE_RADIUS * 0.8;
    }
    slot.from.lerpVectors(pos, ahead, span.t0);
    slot.to.lerpVectors(pos, ahead, span.t1);
    slot.active = true;
    slot.id = shot.id;
    slot.kind = shot.kind;
    const arc = ARC[shot.kind];
    slot.apex = arc.arc > 0 ? arc.lift + arc.arc * slot.from.distanceTo(slot.to) : 0;
    slot.start = shotTick(shot, span.t0);
    slot.end = shotTick(shot, span.t1);
    slot.lands = span.t1 === 1;
    slot.hit = shot.hit;
    slot.onBuilding = !!landsOnBuilding;
    // The damage lands on the unit wherever it is by then: follow it there.
    slot.homing = slot.lands && shot.hit && state.units[shot.targetId] ? shot.targetId : '';
    slot.goal.copy(slot.to);
    // Joined long after it landed (e.g. an adopted snapshot): no stale impact.
    slot.impacted = display - slot.end > STALE_IMPACT_TICKS;
    slot.lastPuff = 0;
    slot.seen = state.tick;
    pools.byId.set(shot.id, slot);
  }

  /** Frees a shot's slot once it's drawn in full; it stays in `done` while it lingers in state. */
  function finish(shot: Drawn): void {
    shot.active = false;
    pools.byId.delete(shot.id);
    pools.done.set(shot.id, lastTickRef.current);
  }

  /** Sparks thrown from `origin`, the fan spun per shot so neighbouring impacts differ. */
  function sparks(
    shot: Drawn,
    origin: THREE.Vector3,
    count: number,
    length: number,
    reach: number,
    now: number
  ): void {
    const spin = (jitter(shot.id, 3) + 0.5) * Math.PI * 2;
    for (let i = 0; i < count; i++) {
      const spark = startEffect(
        pools.effects.spark,
        origin,
        length,
        reach,
        now,
        SPARK_LIFE,
        SPARK_COLORS[shot.kind]
      );
      sparkDirection(i, spin, spark.dir);
    }
  }

  /**
   * A hit flashes white-hot, bursts, throws sparks and lights up its target;
   * a miss kicks up a dust puff and dirt clods, and sparks off the ground.
   */
  function impact(shot: Drawn, now: number): void {
    const visual = IMPACT_VISUALS[shot.kind];
    const { pos } = scratch;
    if (shot.hit) {
      const { hit } = visual;
      startEffect(pools.effects.flash, shot.to, hit.flash, 0, now, HIT_FLASH_LIFE, FLASH_COLOR);
      startEffect(pools.effects.burst, shot.to, hit.burst, 0, now, HIT_BURST_LIFE);
      const target = shot.onBuilding ? TARGET_FLASH.building : TARGET_FLASH.unit;
      pos.set(shot.to.x, GROUND_Y + target.y, shot.to.z);
      startEffect(
        pools.effects.targetFlash,
        pos,
        target.radius,
        0,
        now,
        TARGET_FLASH.life,
        TARGET_FLASH.color
      );
      sparks(shot, shot.to, hit.sparks, hit.sparkLength, hit.sparkReach, now);
      return;
    }
    const { miss } = visual;
    startEffect(pools.effects.dust, shot.to, miss.dust, miss.dustRise, now, DUST_LIFE);
    for (let i = 0; i < Math.min(miss.clods, DIRT_CLODS.length); i++) {
      const clod = DIRT_CLODS[i];
      pos.set(shot.to.x + clod.x * miss.dust, shot.to.y, shot.to.z + clod.z * miss.dust);
      startEffect(pools.effects.dirt, pos, miss.clodSize, clod.up * miss.clodSize, now, CLOD_LIFE);
    }
    sparks(shot, shot.to, miss.sparks, miss.sparkLength, miss.sparkReach, now);
  }

  /**
   * Steers a homing shot's end towards its target unit's position (as
   * UnitsLayer draws it) while the unit's tile is in sight, so the hit bursts
   * on the unit rather than the tile it was aimed at when fired.
   */
  function home(state: SimState, shot: Drawn, delta: number): void {
    const unit = state.units[shot.homing];
    if (unit && !unit.inside && getTileVisibility(getHexTileMapKey(unit.pos)) === 'visible') {
      let q = unit.pos.q;
      let r = unit.pos.r;
      const next = unit.path?.[0];
      if (next) {
        const p = fromMilli(unit.progress ?? 0);
        q += (next.q - q) * p;
        r += (next.r - r) * p;
      }
      const world = axialToWorld({ q, r }, TILE_RADIUS);
      shot.goal.set(world.x, UNIT_HIT_Y, world.z);
    }
    shot.to.lerp(shot.goal, 1 - Math.exp(-12 * delta));
  }

  useFrame(({ clock }, delta) => {
    const state = getState();
    const now = clock.elapsedTime;
    // Once the match is over no new states come: let the clock run on so
    // shots still in flight land instead of freezing mid-air.
    const display =
      state.status === 'ended' && displayRef.current !== null
        ? displayRef.current + delta / DT
        : advanceDisplayTick(displayRef.current ?? state.tick - DISPLAY_LAG, state.tick, delta, DT);
    displayRef.current = display;

    // Once per new state: start new shots, drop the ones gone from it.
    if (state.tick !== lastTickRef.current) {
      lastTickRef.current = state.tick;
      for (const shot of state.shots ?? []) {
        if (pools.done.has(shot.id)) {
          pools.done.set(shot.id, state.tick);
          continue;
        }
        const drawn = pools.byId.get(shot.id);
        if (drawn) drawn.seen = state.tick;
        else track(state, shot, display);
      }
      for (const [id, seen] of pools.done) {
        if (seen !== state.tick) pools.done.delete(id);
      }
      for (const drawn of pools.shots) {
        if (drawn.active && drawn.seen !== state.tick) {
          drawn.active = false;
          pools.byId.delete(drawn.id);
        }
      }
    }

    const { obj, ahead, counts } = scratch;
    const meshes = meshRefs.current;
    for (const name of MESH_NAMES) counts[name] = 0;

    for (const shot of pools.shots) {
      if (!shot.active) continue;
      const length = Math.max(1e-3, shot.end - shot.start);
      const t = (display - shot.start) / length;
      if (t < 0) continue;
      if (shot.homing && !shot.impacted) home(state, shot, delta);
      if (t >= 1 && !shot.impacted) {
        shot.impacted = true;
        if (shot.lands) impact(shot, now);
      }
      obj.rotation.set(0, 0, 0);
      if (shot.kind === 'beam') {
        // The whole line from shooter to target at once, thinning away past impact.
        const fade = (display - shot.start) / (length + BEAM_FADE_TICKS);
        if (fade >= 1) {
          finish(shot);
          continue;
        }
        obj.position.lerpVectors(shot.from, shot.to, 0.5);
        if (shot.from.distanceToSquared(shot.to) > 1e-8) obj.lookAt(shot.to);
        const width = BEAM_WIDTH * (1 - fade);
        obj.scale.set(width, width, shot.from.distanceTo(shot.to));
        put(meshes, counts, 'beam', obj);
        continue;
      }
      if (t >= 1) {
        finish(shot);
        continue;
      }
      along(shot, t, obj.position);
      along(shot, Math.min(1, t + 0.02), ahead);
      if (ahead.distanceToSquared(obj.position) > 1e-8) obj.lookAt(ahead);
      switch (shot.kind) {
        case 'tracer':
          obj.scale.set(0.035, 0.035, Math.min(TRACER_LENGTH, shot.from.distanceTo(shot.to)));
          break;
        case 'arrow':
          obj.scale.set(0.025, 0.025, 0.3);
          break;
        case 'shell':
          obj.scale.setScalar(0.065);
          break;
        case 'missile': {
          obj.scale.set(0.06, 0.06, 0.26);
          // Smoke puffs dropped behind it as it flies (a few at most when first seen mid-flight).
          const step = PUFF_EVERY_TICKS / length;
          shot.lastPuff = Math.max(shot.lastPuff, t - 6 * step);
          while (t - shot.lastPuff >= step) {
            shot.lastPuff += step;
            startEffect(
              pools.effects.puff,
              along(shot, shot.lastPuff, scratch.pos),
              1,
              0.15,
              now,
              PUFF_LIFE
            );
          }
          break;
        }
      }
      put(meshes, counts, shot.kind, obj);
    }

    const { dir, color } = scratch;
    for (const name of EFFECT_NAMES) {
      for (const effect of pools.effects[name]) {
        if (!effect.active) continue;
        const t = (now - effect.start) / effect.life;
        if (t >= 1) {
          effect.active = false;
          continue;
        }
        obj.rotation.set(0, 0, 0);
        obj.position.copy(effect.pos);
        switch (name) {
          case 'flash':
            obj.scale.setScalar(effect.size * flashScale(t));
            break;
          case 'burst':
            obj.scale.setScalar(effect.size * burstScale(t));
            break;
          case 'targetFlash':
            obj.scale.setScalar(effect.size * (1 + 0.25 * t));
            break;
          case 'dust':
            // Billows out and drifts up, thinning away.
            obj.position.y += t * effect.up;
            obj.scale.setScalar(effect.size * dustScale(t));
            break;
          case 'dirt':
            // Thrown up and falling back, shrinking as it settles.
            obj.position.y += effect.up * clodHeight(t);
            obj.scale.setScalar(effect.size * (1 - 0.6 * t));
            break;
          case 'spark': {
            const scale = sparkAt(effect.pos, effect.dir, effect.up, t, obj.position);
            dir.copy(obj.position).add(effect.dir);
            obj.lookAt(dir);
            obj.scale.set(SPARK_WIDTH, SPARK_WIDTH, effect.size * scale);
            break;
          }
          case 'puff':
            // Grows as it drifts up, shrinking away at the end.
            obj.position.y += t * effect.up;
            obj.scale.setScalar(effect.size * (0.05 + 0.12 * t) * (1 - t * t));
            break;
        }
        put(
          meshes,
          counts,
          name,
          obj,
          effect.color ? fadeColor(effect.color, fade(t), color) : undefined
        );
      }
    }

    for (const name of MESH_NAMES) {
      const mesh = meshes[name];
      if (!mesh) continue;
      mesh.count = counts[name];
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    }
  });

  return (
    <>
      {MESH_NAMES.map((name) => {
        const { geometry, material, max, colored } = MESHES[name];
        return (
          <instancedMesh
            key={name}
            ref={(mesh) => {
              if (mesh) {
                mesh.count = 0;
                mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
                if (colored && !mesh.instanceColor) {
                  mesh.instanceColor = new THREE.InstancedBufferAttribute(
                    new Float32Array(max * 3),
                    3
                  );
                  mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
                }
                meshRefs.current[name] = mesh;
              } else {
                delete meshRefs.current[name];
              }
            }}
            args={[geometry, material, max]}
            frustumCulled={false}
            raycast={noRaycast}
          />
        );
      })}
    </>
  );
}
